import { useDebouncedCallback } from 'use-debounce';
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAutosaveFeedback } from '../../components/ReactFlow/useAutosaveFeedback';
import { useStaleEditorCheck } from '../../components/ReactFlow/useStaleEditorCheck';
import { useLoadLatest } from '../../components/ReactFlow/useLoadLatest';
import { ResourceEditorContext } from '../../EditorWithProviders';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useResourceByQueryId } from '../../hooks';
import {
  Box,
  Editable,
  EditablePreview,
  EditableInput,
  Heading,
  Spinner,
  Stack,
  Center,
} from '@chakra-ui/react';
import { Resource } from '@duke-hq/svc-api-db';
import { useDukeFileUpload } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/blocknode/useDukeFileUpload';
import { DukeTiptapEditor } from '@duke-hq/ui/components/DukeTiptapEditor/DukeTiptapEditor';
import { JSONContent } from '@tiptap/core';
import type { Editor as TiptapEditor } from '@tiptap/react';
import { t } from '@lingui/core/macro';
import { useSnapshot } from 'valtio';
import { EditorLayout } from '../../navbar/EditorLayout';
import { EditorStatus } from '../../navbar/EditorStatus';
import { TranslationsToolbarPill } from '../../components/translations/TranslationsToolbarPill';
import { CanvasLanguageBar } from '../../components/translations/CanvasView/CanvasLanguageBar';
import { useUnitGuideTranslation } from '../../components/translations/UnitGuideView/useUnitGuideTranslation';
import { OverrideWorkbenchModal } from '../../components/translations/OverrideEditor/OverrideWorkbenchModal';
import { languageDir } from '@duke-hq/shared-constants/courseTranslationLanguages';
import { EditorBreadcrumbs } from '../../navbar/EditorBreadcrumbs';
import { EditableResourceTitle } from '../../navbar/EditableResourceTitle';
import { PagePublishButton } from '../../navbar/PagePublishButton';
import { FlowVersionHistory } from '../../navbar/FlowVersionHistory';
import { PageEditorStateContext } from '../../components/ReactFlow/PageEditorStateContext';
import {
  dukeTTEditorState,
  EditorSavingStateEnum,
} from '../../components/ReactFlow/dukeTTEditorState';

// Cancel the shared tiptap CSS's horizontal inset so the document lines up with
// the heading (and editable/preview widths match) as one centred column.
const flushEditorInset = {
  '.duke-tiptap-default, .duke-tiptap-readonly': { mx: 0 },
  '.duke-tiptap-editor.tiptap': { px: 0 },
};

// Nearest scrollable ancestor. The page scroll lives on EditorLayout's content
// column, not on the editor, so entering/leaving the preview (which swaps
// editors) needs to read and restore scroll here to keep the reader's place.
function findScrollParent(element: HTMLElement | null): HTMLElement | null {
  let node = element?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// TODO: this component mixes `resource` and `resourceQuery.data` in ways that
// are confusing. It's not clear what either actually is, and which one should be used.
export function UnitGuideEditor({
  initialContent,
  resource,
}: {
  initialContent: JSONContent;
  resource: Resource;
}) {
  const resourceQuery = useResourceByQueryId();
  const { resourceVersionId } = useContext(ResourceEditorContext);
  const { lastPublishedResourceVersion } = useContext(PageEditorStateContext);
  const autosaveFeedback = useAutosaveFeedback('unit guide');
  const updateResource = trpcAdmin.resources.update.useMutation({
    onSuccess: () => {
      void resourceQuery.refetch();
    },
  });

  const updateResourceVersion = trpcAdmin.resourceVersions.update.useMutation({
    onSuccess: () => {
      void resourceQuery.refetch();
    },
  });

  // The draft revision this editor is saving from, sent on every save so the
  // server can reject a stale write (another tab or editor saved since we
  // loaded), and updated from each successful save so subsequent saves stay in sync.
  const revisionRef = useRef(resourceQuery.data?.draftVersion?.revision ?? 0);

  const { isStale } = useStaleEditorCheck({
    resourceVersionId,
    getLocalRevision: () => revisionRef.current,
  });
  const { requestLoadLatest, confirmDialog: loadLatestConfirmDialog } = useLoadLatest('unit guide');

  // The Tiptap instance, captured once the editor mounts, so the toolbar can
  // drive undo/redo from outside the editor. `canUndo`/`canRedo` aren't reactive
  // on the instance, so we mirror them into state and refresh on each
  // transaction, which is what keeps the toolbar buttons enabling/disabling live.
  const [tiptapEditor, setTiptapEditor] = useState<TiptapEditor | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const onEditorReady = useCallback((editor: TiptapEditor) => {
    setTiptapEditor(editor);
  }, []);

  useEffect(() => {
    if (!tiptapEditor) {
      return;
    }
    const sync = () => {
      setCanUndo(tiptapEditor.can().undo());
      setCanRedo(tiptapEditor.can().redo());
    };
    sync();
    tiptapEditor.on('transaction', sync);
    return () => {
      tiptapEditor.off('transaction', sync);
    };
  }, [tiptapEditor]);

  const handleUpdate = useDebouncedCallback(async ({ content }: { content: JSONContent }) => {
    // Once a save has been rejected as stale, every later autosave conflicts
    // again until the page is reloaded (we never advance revisionRef past a
    // conflict). Stop re-firing them so one stale tab raises a single conflict.
    if (autosaveFeedback.hasConflict()) {
      return;
    }

    autosaveFeedback.onSaving();
    try {
      const saved = await updateResourceVersion.mutateAsync({
        resourceVersionId,
        content,
        baseRevision: revisionRef.current,
      });
      revisionRef.current = saved.revision;
      autosaveFeedback.onSaved();
    } catch (error) {
      // On conflict we do not advance revisionRef: a refresh is the recovery path.
      autosaveFeedback.onSaveError(error);
    }
  }, 3000);

  const onChange = (content: JSONContent) => {
    autosaveFeedback.onUnsaved();
    void handleUpdate({ content });
  };

  // The title shown by both the toolbar field and the body heading, kept in one
  // piece of state so the two never disagree. Editing the body heading updates it
  // live (its onChange fires per keystroke); the toolbar field commits on
  // blur/Enter. A committed rename reconciles it with the server via the refetch.
  // Seeded from the query and re-synced when the server title changes from under
  // us (e.g. another tab renamed it), but never while the user is mid-edit.
  const [displayTitle, setDisplayTitle] = useState(resourceQuery.data?.title ?? '');
  const isRenamingRef = useRef(false);
  const serverTitle = resourceQuery.data?.title;
  useEffect(() => {
    if (!isRenamingRef.current && typeof serverTitle === 'string') {
      setDisplayTitle(serverTitle);
    }
  }, [serverTitle]);

  // Single rename path shared by the toolbar title and the body heading: an
  // optimistic update keeps the typed value on screen, the save drives the same
  // Saving/Saved indicator as a content edit, and the refetch reconciles both
  // views. Rolls back on failure.
  const renameResource = async (value: string) => {
    const id = resourceQuery.data?.id;
    if (id == null) {
      return;
    }
    const previousTitle = displayTitle;
    setDisplayTitle(value);
    isRenamingRef.current = true;
    autosaveFeedback.onSaving();
    try {
      await updateResource.mutateAsync({ id, title: value });
      autosaveFeedback.onSaved();
    } catch (error) {
      setDisplayTitle(previousTitle);
      autosaveFeedback.onSaveError(error);
      // The rolled-back title leaves nothing to save, so don't strand the
      // indicator on "Unsaved changes"; settle it back to idle.
      autosaveFeedback.onIdle();
      throw error;
    } finally {
      isRenamingRef.current = false;
    }
  };

  const { uploadFile } = useDukeFileUpload({
    fileAttributes: {
      attachedToKind: 'RESOURCE',
      attachedToId: resource.id,
    },
  });

  const data = resourceQuery.data;
  const isReady =
    typeof data === 'object' && data != null && 'title' in data && 'id' in data && data.id != null;

  // Guarded on resourceId because the publish context persists across resources,
  // so an unguarded value would read one guide's translations into another.
  const publishedResourceVersionId =
    lastPublishedResourceVersion != null &&
    data?.id != null &&
    lastPublishedResourceVersion.resourceId === data.id
      ? lastPublishedResourceVersion.id
      : data?.last_published_version_id;

  // The unit guide tracks dirtiness via the shared saving-state indicator, not
  // flowEditorState; the version-history popover needs it for the restore confirm.
  const { state: savingState } = useSnapshot(dukeTTEditorState);
  const draftIsDirty = savingState === EditorSavingStateEnum.UNSAVED_CHANGES;

  // Translation preview lens: target language + an English toggle. Null = editing.
  const [lensTarget, setLensTarget] = useState<string | null>(null);
  const [lensShowingEnglish, setLensShowingEnglish] = useState(false);

  // Entering/leaving the preview swaps between two separately-keyed editors, which
  // remounts and would jump the page to the top. Snapshot the scroll position on
  // toggle and restore it once the incoming editor has laid out (below), so the
  // reader keeps their place across the swap.
  const bodyRef = useRef<HTMLDivElement>(null);
  const pendingScrollTop = useRef<number | null>(null);
  const captureScroll = () => {
    pendingScrollTop.current = findScrollParent(bodyRef.current)?.scrollTop ?? null;
  };

  const openLens = useCallback((language: string) => {
    captureScroll();
    setLensTarget(language);
    setLensShowingEnglish(false);
  }, []);
  const closeLens = useCallback(() => {
    captureScroll();
    setLensTarget(null);
    setLensShowingEnglish(false);
  }, []);

  const [editLanguage, setEditLanguage] = useState<string | null>(null);
  const goToWorkbench = useCallback((language: string) => {
    setEditLanguage(language);
  }, []);

  // The whole lens is read-only (English toggle included), matching the canvas.
  const lensOpen = lensTarget != null;

  // Restore the scroll captured on toggle. The incoming editor mounts empty and
  // fills in via setContent a tick later, so the page isn't full height on the
  // first frame; restore on the next frame, once content has laid out.
  useLayoutEffect(() => {
    if (pendingScrollTop.current === null) {
      return;
    }
    const target = pendingScrollTop.current;
    pendingScrollTop.current = null;
    const scrollParent = findScrollParent(bodyRef.current);
    if (!scrollParent) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      scrollParent.scrollTop = target;
    });
    return () => cancelAnimationFrame(frame);
  }, [lensOpen]);

  const lensLanguage = lensShowingEnglish ? null : lensTarget;
  const translation = useUnitGuideTranslation({
    content: initialContent,
    resourceVersionId: publishedResourceVersionId ?? 0,
    language: lensLanguage,
    enabled: lensLanguage != null && publishedResourceVersionId != null,
  });

  // Null while loading, on error, or when showing English; otherwise the
  // per-segment overlay (segments edited since publish stay English, flagged).
  const translatedContent =
    lensTarget != null && !lensShowingEnglish ? translation.translatedContent : null;
  const showingTranslated = translatedContent != null;
  const previewContent = translatedContent ?? initialContent;
  const previewDir = lensTarget != null && showingTranslated ? languageDir(lensTarget) : 'ltr';

  return (
    <EditorLayout
      toolbar={{
        breadcrumbs: (
          <EditorBreadcrumbs
            crumbs={[
              { label: t`Units`, to: '/units' },
              ...(data?.unitOfCompetency
                ? [
                    {
                      label: data.unitOfCompetency.code || data.unitOfCompetency.title,
                      to: `/units/${data.unitOfCompetency.id}`,
                    },
                  ]
                : []),
            ]}
            current={
              <EditableResourceTitle
                title={displayTitle}
                onRename={renameResource}
                ariaLabel={t`Unit guide title`}
              />
            }
          />
        ),
        undoRedo: {
          canUndo,
          canRedo,
          onUndo: () => tiptapEditor?.chain().focus().undo().run(),
          onRedo: () => tiptapEditor?.chain().focus().redo().run(),
        },
        staleEditor: { isStale, entityLabel: 'unit guide', onLoadLatest: requestLoadLatest },
        status: (
          <EditorStatus
            published={{
              isPublished: data?.last_published_version_id != null,
              publishedVersion: data?.lastPublishedVersion?.version,
              publishedAt: data?.lastPublishedVersion?.published_at,
              hasUnpublishedChanges: data?.hasUnpublishedChanges,
            }}
            versionHistory={
              data?.id != null ? (
                <FlowVersionHistory
                  resourceId={data.id}
                  draftVersionId={data.draftVersion?.id}
                  draftRevision={data.draftVersion?.revision ?? 0}
                  livePublishedVersion={data.lastPublishedVersion?.version}
                  draftIsDirty={draftIsDirty}
                  getBaseRevision={() => revisionRef.current}
                />
              ) : undefined
            }
            derivedOutputs={
              <TranslationsToolbarPill
                resourceId={data?.id}
                resourceVersionId={publishedResourceVersionId}
                resourceKind="PAGE"
                publishedVersion={data?.lastPublishedVersion?.version}
                onViewLanguage={openLens}
                onEditLanguage={goToWorkbench}
              />
            }
          />
        ),
        actions: [{ id: 'publish', node: <PagePublishButton /> }],
      }}
      body={{
        kind: 'content',
        maxW: '3xl',
        children: isReady ? (
          // The heading sits tight above the document (gap 2), distinct from the
          // layout's section rhythm.
          <Stack spacing="2" ref={bodyRef}>
            {lensTarget != null ? (
              // Zero-height sticky rail: follows the scroll but reserves no space,
              // so opening the preview doesn't gap the heading or reflow the page.
              <Box
                key="language-bar"
                position="sticky"
                top={3}
                zIndex={10}
                h={0}
                mt={-2}
                overflow="visible"
              >
                <CanvasLanguageBar
                  position="static"
                  language={lensTarget}
                  showingEnglish={lensShowingEnglish}
                  isLoading={translation.isLoading}
                  isError={translation.isError}
                  editedCount={lensShowingEnglish ? 0 : translation.editedCount}
                  onShowEnglish={() => setLensShowingEnglish(true)}
                  onShowTarget={() => setLensShowingEnglish(false)}
                  onEditTranslation={() => goToWorkbench(lensTarget)}
                  onClose={closeLens}
                />
              </Box>
            ) : null}

            <Editable
              key="title"
              value={displayTitle}
              as={Heading}
              fontSize="3xl"
              fontWeight="medium"
              onChange={setDisplayTitle}
              onSubmit={(value) => {
                void renameResource(value);
              }}
            >
              <EditablePreview />
              <EditableInput />
            </Editable>

            {lensOpen ? (
              // Distinct key from the editable editor so React unmounts one and
              // mounts the other rather than reconciling ProseMirror-owned DOM
              // across the boundary (which crashes with removeChild). Stable across
              // the English<->target toggle so switching swaps content in place
              // without remounting and losing the reader's scroll position.
              <Box key="preview" dir={previewDir} sx={flushEditorInset}>
                <DukeTiptapEditor initialContent={previewContent} onChange={() => {}} readOnly />
              </Box>
            ) : (
              <Box key="editable" sx={flushEditorInset}>
                <DukeTiptapEditor
                  initialContent={initialContent}
                  onChange={onChange}
                  uploadFn={uploadFile}
                  onEditorReady={onEditorReady}
                />
              </Box>
            )}
          </Stack>
        ) : (
          <Center width="100%" py="10">
            <Spinner />
          </Center>
        ),
      }}
    >
      {loadLatestConfirmDialog}
      {editLanguage != null && data?.id != null && publishedResourceVersionId != null ? (
        <OverrideWorkbenchModal
          isOpen
          onClose={() => setEditLanguage(null)}
          resourceId={data.id}
          // Translations key on the published version; same id the lens reads.
          resourceVersionId={publishedResourceVersionId}
          resourceKind="PAGE"
          publishedVersion={data.lastPublishedVersion?.version ?? null}
          language={editLanguage}
        />
      ) : null}
    </EditorLayout>
  );
}
